import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Bookmark, BookmarkCheck, Loader2 } from "lucide-react";
import Modal from "@/components/Modal";
import { SubscribeOrNotAPI } from "@/services/api/user/subscribeEvent";

interface StudentSubscribeButtonProps {
  eventId: string;
  subscribed: boolean;
  onChange?: (subscribed: boolean) => void;
  variant?: "icon" | "full";
}

/**
 * Subscribe / unsubscribe toggle backed by the `subscriptEvent` endpoints.
 * Renders as a compact bookmark icon inside feed cards, or as a full-width
 * call-to-action on the event details screen.
 */
const StudentSubscribeButton: React.FC<StudentSubscribeButtonProps> = ({
  eventId,
  subscribed,
  onChange,
  variant = "icon",
}) => {
  const { t } = useTranslation();
  const [pending, setPending] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  const handleToggle = async () => {
    if (pending) return;
    setPending(true);
    setErrorText(null);
    const next = !subscribed;
    const resp = await SubscribeOrNotAPI(subscribed, eventId);
    setPending(false);
    if (resp?.status === 0) {
      onChange?.(next);
      return;
    }
    setErrorText(t("Something went wrong"));
  };

  const icon = pending ? (
    <Loader2 size={variant === "full" ? 18 : 16} className="animate-spin" />
  ) : subscribed ? (
    <BookmarkCheck size={variant === "full" ? 18 : 16} />
  ) : (
    <Bookmark size={variant === "full" ? 18 : 16} />
  );

  return (
    <>
      {variant === "full" ? (
        <button
          type="button"
          onClick={handleToggle}
          disabled={pending}
          className={`inline-flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold transition disabled:opacity-60 ${
            subscribed
              ? "bg-white text-[#ffa217] ring-2 ring-[#ffa217] hover:bg-[#fff8f0]"
              : "bg-[#ffa217] text-white hover:bg-[#f59200]"
          }`}
        >
          {icon}
          {subscribed ? t("Subscribed") : t("Subscribe")}
        </button>
      ) : (
        <button
          type="button"
          onClick={handleToggle}
          disabled={pending}
          aria-label={subscribed ? t("Subscribed") : t("Subscribe")}
          title={subscribed ? t("Subscribed") : t("Subscribe")}
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition disabled:opacity-60 ${
            subscribed
              ? "border-[#ffa217] bg-[#fff3e0] text-[#ffa217]"
              : "border-gray-200 bg-white text-gray-400 hover:border-[#ffa217] hover:text-[#ffa217]"
          }`}
        >
          {icon}
        </button>
      )}

      <Modal visible={Boolean(errorText)} onClose={() => setErrorText(null)}>
        <div className="rounded-2xl bg-white p-5 text-center shadow-lg">
          <div className="mb-1.5 text-base font-semibold text-gray-900">
            {t("Something went wrong")}
          </div>
          <p className="mb-4 text-sm text-gray-500">
            {t("Please try again later.")}
          </p>
          <button
            type="button"
            onClick={() => setErrorText(null)}
            className="w-full rounded-xl bg-[#ffa217] py-2.5 text-sm font-bold text-white"
          >
            {t("OK")}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default StudentSubscribeButton;
